import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, FileUp, Plus, Trash2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { useVendorAuth, vendorFetch } from "./VendorAuthContext";
import { API_BASE } from "../DataContext";

const inr = (n) => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2, minimumFractionDigits: 2 });
const fmtDate = (iso) => {
  if (!iso) return "—";
  const [y, m, d] = String(iso).split("-");
  return y && m && d ? `${d}-${m}-${y}` : iso;
};

const MISC_PRESETS = ["Transport Charges", "Packing Charges", "Loading Charges", "Freight", "P & F Charges", "Insurance"];

export default function VendorSubmitInvoice() {
  const { token } = useVendorAuth();
  const nav = useNavigate();
  const [pendingPOs, setPendingPOs] = useState([]);
  const [step, setStep] = useState(1);
  const [poId, setPoId] = useState("");
  const [header, setHeader] = useState({ invoice_number: "", invoice_date: new Date().toISOString().slice(0, 10), vehicle_no: "", lr_no: "", remarks: "" });
  const [pdf, setPdf] = useState(null);
  const [lines, setLines] = useState({}); // { item_index: qty }
  const [miscCharges, setMiscCharges] = useState([]); // [{description, qty, rate, gst_pct}]
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    vendorFetch(token)("/api/vendor/pending-pos").then((r) => r.json()).then(setPendingPOs);
  }, [token]);

  const selectedPO = useMemo(() => pendingPOs.find((p) => p.id === poId), [pendingPOs, poId]);

  const balances = useMemo(() => {
    if (!selectedPO) return {};
    const out = {};
    (selectedPO.items || []).forEach((it, i) => {
      // Balance is now driven by INVOICED qty (not GRN receipts) so excess is
      // measured against remaining PO capacity for future invoices.
      const invd = Number((selectedPO.invoiced_by_index || {})[i] || 0);
      out[i] = Number(it.qty) - invd;   // can be negative if already over-invoiced
    });
    return out;
  }, [selectedPO]);

  const excessTotal = useMemo(() => {
    if (!selectedPO) return 0;
    let e = 0;
    (selectedPO.items || []).forEach((it, i) => {
      const q = Number(lines[i] || 0);
      const bal = balances[i] || 0;
      if (q > bal) e += (q - bal);
    });
    return e;
  }, [lines, balances, selectedPO]);

  const invoiceTotals = useMemo(() => {
    if (!selectedPO) return { productBasic: 0, productGst: 0, miscBasic: 0, miscGst: 0, totalBasic: 0, totalGst: 0, grandTotal: 0 };
    let productBasic = 0, productGst = 0;
    Object.entries(lines).forEach(([i, q]) => {
      const it = selectedPO.items[Number(i)];
      const qty = Number(q) || 0;
      if (!it || qty <= 0) return;
      const basic = qty * Number(it.rate || 0);
      productBasic += basic;
      productGst += basic * (Number(it.gst_pct || 0) / 100);
    });
    let miscBasic = 0, miscGst = 0;
    miscCharges.forEach((m) => {
      const qty = Number(m.qty || 0), rate = Number(m.rate || 0);
      const basic = qty * rate;
      miscBasic += basic;
      miscGst += basic * (Number(m.gst_pct || 0) / 100);
    });
    const totalBasic = productBasic + miscBasic;
    const totalGst = productGst + miscGst;
    return {
      productBasic, productGst, miscBasic, miscGst,
      totalBasic, totalGst, grandTotal: totalBasic + totalGst,
    };
  }, [lines, miscCharges, selectedPO]);
  const invoiceAmount = invoiceTotals.totalBasic;  // Step 3 "Basic Invoice Total (excl. GST)" for products only kept below

  const addMisc = (preset = "") => setMiscCharges((prev) => [...prev, { description: preset, qty: 1, rate: 0, gst_pct: 18 }]);
  const updateMisc = (i, patch) => setMiscCharges((prev) => prev.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  const removeMisc = (i) => setMiscCharges((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async () => {
    setError("");
    if (!pdf) return setError("Please attach the invoice PDF (mandatory)");
    const items = Object.entries(lines).filter(([, q]) => Number(q) > 0).map(([i, q]) => ({ item_index: Number(i), invoice_qty: Number(q) }));
    if (items.length === 0) return setError("Enter invoice quantity for at least one item");
    // Validate misc rows (skip empty descriptions silently; error only if row has values but no description)
    for (const m of miscCharges) {
      if (!m.description?.trim() && (Number(m.rate) > 0 || Number(m.qty) > 1)) {
        return setError("Every misc charge row needs a description");
      }
    }
    const miscClean = miscCharges
      .filter((m) => m.description?.trim() && Number(m.qty) > 0)
      .map((m) => ({ description: m.description.trim(), qty: Number(m.qty), rate: Number(m.rate), gst_pct: Number(m.gst_pct || 0) }));
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("po_id", poId);
      fd.append("invoice_number", header.invoice_number);
      fd.append("invoice_date", header.invoice_date);
      fd.append("vehicle_no", header.vehicle_no);
      fd.append("lr_no", header.lr_no);
      fd.append("remarks", header.remarks);
      fd.append("items", JSON.stringify(items));
      fd.append("misc_charges", JSON.stringify(miscClean));
      fd.append("pdf", pdf);
      const r = await fetch(`${API_BASE}/api/vendor/invoices`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
      if (!r.ok) throw new Error((await r.json()).detail || "Submission failed");
      nav("/vendor/invoices?submitted=1");
    } catch (e) { setError(e.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6" data-testid="vendor-submit-invoice">
      <div>
        <h1 className="text-2xl md:text-3xl font-black text-[#0a2a5e]">Submit Invoice</h1>
        <p className="text-sm text-slate-500 mt-1">Three-step wizard. Fields marked with * are mandatory.</p>
      </div>

      <StepIndicator step={step} />

      {/* STEP 1: Select PO */}
      {step === 1 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-6" data-testid="invoice-step-1">
          <h2 className="font-bold text-slate-800 mb-4">Step 1 · Select a Purchase Order</h2>
          {pendingPOs.length === 0 && (
            <div className="text-center py-12 text-slate-500">
              <div className="mx-auto h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center mb-3">
                <FileUp className="h-6 w-6 text-slate-400" />
              </div>
              <p className="font-semibold text-slate-700 mb-1">No pending Purchase Orders</p>
              <p className="text-sm">You have no Purchase Orders with unbilled quantity. New POs from BAISHALII will appear here.</p>
            </div>
          )}
          <div className="space-y-2">
            {pendingPOs.map((p) => (
              <label key={p.id} className={`flex items-center gap-3 p-3 border rounded-xl cursor-pointer ${poId === p.id ? "border-[#0a2a5e] bg-sky-50" : "border-slate-200 hover:bg-slate-50"}`}>
                <input type="radio" name="po" value={p.id} checked={poId === p.id} onChange={() => setPoId(p.id)} data-testid={`inv-po-${p.id}`} className="h-4 w-4 text-[#0a2a5e]" />
                <div className="flex-1">
                  <div className="font-mono text-sm font-semibold text-[#0a2a5e]">{p.po_number}</div>
                  <div className="text-xs text-slate-500">{fmtDate(p.po_date)} · Pending: {p.summary.balance_qty} units, ₹ {inr(p.summary.balance_value)}</div>
                </div>
              </label>
            ))}
          </div>
          <div className="flex justify-end mt-6">
            <Button onClick={() => setStep(2)} disabled={!poId} data-testid="invoice-next-1" className="bg-[#0a2a5e] hover:bg-[#0a2a5e]/90 text-white rounded-full">Next <ArrowRight className="h-4 w-4 ml-1" /></Button>
          </div>
        </div>
      )}

      {/* STEP 2: Invoice header + PDF */}
      {step === 2 && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-6" data-testid="invoice-step-2">
          <h2 className="font-bold text-slate-800 mb-4">Step 2 · Invoice Details</h2>
          <div className="grid md:grid-cols-2 gap-3">
            <Field label="Invoice Number *"><Input value={header.invoice_number} onChange={(e) => setHeader({ ...header, invoice_number: e.target.value })} data-testid="invoice-number" /></Field>
            <Field label="Invoice Date *"><Input type="date" value={header.invoice_date} onChange={(e) => setHeader({ ...header, invoice_date: e.target.value })} data-testid="invoice-date" /></Field>
            <Field label="Vehicle No"><Input value={header.vehicle_no} onChange={(e) => setHeader({ ...header, vehicle_no: e.target.value })} /></Field>
            <Field label="LR Number"><Input value={header.lr_no} onChange={(e) => setHeader({ ...header, lr_no: e.target.value })} /></Field>
            <div className="md:col-span-2">
              <Field label="Remarks">
                <textarea value={header.remarks} onChange={(e) => setHeader({ ...header, remarks: e.target.value })} rows={2} className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm" />
              </Field>
            </div>
            <div className="md:col-span-2">
              <label className="block text-xs font-semibold text-slate-500 mb-1">Invoice PDF *</label>
              <label htmlFor="pdf-upload" className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 rounded-xl px-4 py-6 cursor-pointer hover:border-[#0a2a5e] hover:bg-sky-50 transition-colors">
                <FileUp className="h-5 w-5 text-slate-400" />
                <span className="text-sm text-slate-600">{pdf ? pdf.name : "Click to upload invoice PDF"}</span>
              </label>
              <input id="pdf-upload" type="file" accept="application/pdf" data-testid="invoice-pdf" onChange={(e) => setPdf(e.target.files?.[0] || null)} className="hidden" />
            </div>
          </div>
          <div className="flex justify-between mt-6">
            <Button variant="outline" onClick={() => setStep(1)} className="rounded-full"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
            <Button onClick={() => setStep(3)} disabled={!header.invoice_number || !header.invoice_date || !pdf} data-testid="invoice-next-2" className="bg-[#0a2a5e] hover:bg-[#0a2a5e]/90 text-white rounded-full">Next <ArrowRight className="h-4 w-4 ml-1" /></Button>
          </div>
        </div>
      )}

      {/* STEP 3: Quantities */}
      {step === 3 && selectedPO && (
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4 md:p-6" data-testid="invoice-step-3">
          <h2 className="font-bold text-slate-800 mb-4">Step 3 · Invoice Quantities</h2>
          {excessTotal > 0 && (
            <div data-testid="excess-banner" className="mb-4 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3">
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-amber-500 text-white text-xs font-bold shrink-0">!</span>
              <div className="text-sm">
                <div className="font-semibold text-amber-900">You're invoicing more than the PO balance</div>
                <div className="text-amber-800">Total excess: <strong>+{inr(excessTotal)}</strong> across flagged rows. Submission is allowed — the excess will be recorded and shown to the admin.</div>
              </div>
            </div>
          )}
          <div className="overflow-x-auto border border-slate-200 rounded-xl">
            <table className="w-full text-sm min-w-[720px]">
              <thead>
                <tr className="bg-slate-50 text-xs text-slate-500 uppercase text-left">
                  <th className="px-3 py-2">Item</th>
                  <th className="px-3 py-2 text-right">Balance Qty</th>
                  <th className="px-3 py-2 text-right w-32">Invoice Qty</th>
                  <th className="px-3 py-2 text-right">Rate</th>
                  <th className="px-3 py-2 text-right">Amount</th>
                </tr>
              </thead>
              <tbody>
                {selectedPO.items.map((it, i) => {
                  const q = Number(lines[i] || 0);
                  const rate = Number(it.rate || 0);
                  const lineAmt = q * rate;                 // basic Qty × Rate (excl. GST)
                  const balance = balances[i] || 0;
                  const isExcess = q > balance && q > 0;
                  const excessAmt = isExcess ? q - balance : 0;
                  return (
                    <tr key={i} className={`border-t border-slate-100 ${isExcess ? "bg-red-50/60" : ""}`}>
                      <td className="px-3 py-2">
                        <div className="font-semibold">{it.item}</div>
                        {it.description && <div className="text-xs text-slate-500">{it.description}</div>}
                      </td>
                      <td className={`px-3 py-2 text-right font-semibold ${balance > 0 ? "text-emerald-700" : "text-slate-400"}`}>{balance} {it.unit}</td>
                      <td className="px-3 py-2">
                        <input type="number" step="any" min="0" value={lines[i] || ""}
                          onChange={(e) => setLines({ ...lines, [i]: e.target.value })}
                          data-testid={`invoice-qty-${i}`}
                          className={`w-full px-2 py-1 rounded border text-right text-sm ${isExcess ? "border-red-400 focus:border-red-500" : "border-slate-200"}`} />
                        {isExcess && (
                          <div data-testid={`excess-badge-${i}`} className="mt-1 inline-flex items-center gap-1 rounded-full bg-red-100 text-red-700 text-[10px] font-bold px-2 py-0.5">
                            EXCESS +{inr(excessAmt)}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">₹ {inr(it.rate)}</td>
                      <td className="px-3 py-2 text-right font-semibold">₹ {inr(lineAmt)}</td>
                    </tr>
                  );
                })}
                <tr className="border-t-2 border-[#0a2a5e] bg-sky-50">
                  <td colSpan={4} className="px-3 py-3 text-right font-bold text-[#0a2a5e]">Products Basic (excl. GST)</td>
                  <td className="px-3 py-3 text-right text-lg font-bold text-[#0a2a5e]" data-testid="invoice-total">₹ {inr(invoiceTotals.productBasic)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Miscellaneous Charges */}
          <div className="mt-6" data-testid="misc-charges-section">
            <div className="flex items-center justify-between mb-2">
              <div>
                <h3 className="text-sm font-bold text-slate-800">Miscellaneous / Additional Charges</h3>
                <p className="text-[11px] text-slate-500">Transport, Packing, Loading, Freight etc. These are taxable but do NOT affect PO quantity/balance.</p>
              </div>
              <div className="flex items-center gap-1.5 flex-wrap justify-end">
                {MISC_PRESETS.slice(0, 4).map((p) => (
                  <button key={p} type="button" onClick={() => addMisc(p)}
                    className="rounded-full bg-slate-100 hover:bg-sky-100 text-slate-600 hover:text-sky-700 text-[11px] font-semibold px-2.5 py-1 transition"
                    data-testid={`misc-preset-${p.replace(/\s/g,'-').toLowerCase()}`}>
                    + {p}
                  </button>
                ))}
                <Button type="button" onClick={() => addMisc("")} data-testid="misc-add-blank-btn"
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-xs h-7 px-3">
                  <Plus className="h-3 w-3 mr-0.5" /> Add row
                </Button>
              </div>
            </div>
            {miscCharges.length === 0 ? (
              <div className="text-xs text-slate-400 italic border border-dashed border-slate-200 rounded-lg px-3 py-4 text-center">
                No miscellaneous charges added. Use the buttons above if applicable.
              </div>
            ) : (
              <div className="overflow-x-auto border border-slate-200 rounded-xl">
                <table className="w-full text-sm min-w-[720px]">
                  <thead>
                    <tr className="bg-amber-50 text-xs text-amber-800 uppercase text-left">
                      <th className="px-3 py-2">Description *</th>
                      <th className="px-3 py-2 text-right w-20">Qty</th>
                      <th className="px-3 py-2 text-right w-28">Rate (₹)</th>
                      <th className="px-3 py-2 text-right w-24">GST %</th>
                      <th className="px-3 py-2 text-right w-32">Amount + GST</th>
                      <th className="px-3 py-2 w-12"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {miscCharges.map((m, i) => {
                      const q = Number(m.qty || 0), r = Number(m.rate || 0);
                      const basic = q * r, gst = basic * Number(m.gst_pct || 0) / 100;
                      return (
                        <tr key={i} className="border-t border-slate-100" data-testid={`misc-row-${i}`}>
                          <td className="px-3 py-2">
                            <input type="text" value={m.description} onChange={(e) => updateMisc(i, { description: e.target.value })}
                              placeholder="e.g. Transport Charges"
                              className="w-full px-2 py-1 rounded border border-slate-200 text-sm"
                              data-testid={`misc-desc-${i}`} />
                          </td>
                          <td className="px-3 py-2">
                            <input type="number" step="any" min="0" value={m.qty}
                              onChange={(e) => updateMisc(i, { qty: e.target.value })}
                              className="w-full px-2 py-1 rounded border border-slate-200 text-right text-sm"
                              data-testid={`misc-qty-${i}`} />
                          </td>
                          <td className="px-3 py-2">
                            <input type="number" step="any" min="0" value={m.rate}
                              onChange={(e) => updateMisc(i, { rate: e.target.value })}
                              className="w-full px-2 py-1 rounded border border-slate-200 text-right text-sm"
                              data-testid={`misc-rate-${i}`} />
                          </td>
                          <td className="px-3 py-2">
                            <input type="number" step="any" min="0" max="28" value={m.gst_pct}
                              onChange={(e) => updateMisc(i, { gst_pct: e.target.value })}
                              className="w-full px-2 py-1 rounded border border-slate-200 text-right text-sm"
                              data-testid={`misc-gst-${i}`} />
                          </td>
                          <td className="px-3 py-2 text-right text-sm">
                            <div className="font-semibold">₹ {inr(basic + gst)}</div>
                            <div className="text-[10px] text-slate-500">basic ₹{inr(basic)} + gst ₹{inr(gst)}</div>
                          </td>
                          <td className="px-3 py-2 text-center">
                            <button type="button" onClick={() => removeMisc(i)} className="text-red-500 hover:bg-red-50 rounded p-1" data-testid={`misc-remove-${i}`} title="Remove row">
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Grand Totals */}
          <div className="mt-6 bg-slate-50 border border-slate-200 rounded-xl p-4 max-w-md ml-auto" data-testid="invoice-totals-summary">
            <TotalRow label="Products Basic" value={invoiceTotals.productBasic} />
            {invoiceTotals.miscBasic > 0 && <TotalRow label="Misc / Additional Basic" value={invoiceTotals.miscBasic} testid="total-misc-basic" />}
            <TotalRow label="Total Basic (excl. GST)" value={invoiceTotals.totalBasic} bold testid="total-basic" />
            <TotalRow label="Total GST" value={invoiceTotals.totalGst} testid="total-gst" />
            <div className="border-t-2 border-[#0a2a5e] mt-2 pt-2">
              <TotalRow label="Invoice Total (incl. GST)" value={invoiceTotals.grandTotal} big testid="invoice-grand-total" />
            </div>
          </div>
          {error && <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2 mt-4">{error}</div>}
          <div className="flex justify-between mt-6">
            <Button variant="outline" onClick={() => setStep(2)} className="rounded-full"><ArrowLeft className="h-4 w-4 mr-1" /> Back</Button>
            <Button onClick={submit} disabled={submitting} data-testid="invoice-submit-btn" className="bg-[#0a2a5e] hover:bg-[#0a2a5e]/90 text-white rounded-full">
              <Check className="h-4 w-4 mr-1" /> {submitting ? "Submitting…" : "Submit Invoice"}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

const Field = ({ label, children }) => (
  <div>
    <label className="block text-xs font-semibold text-slate-500 mb-1">{label}</label>
    {children}
  </div>
);

const StepIndicator = ({ step }) => (
  <div className="flex items-center gap-2 text-sm">
    {[1, 2, 3].map((n) => (
      <React.Fragment key={n}>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full ${step >= n ? "bg-[#0a2a5e] text-white" : "bg-slate-200 text-slate-500"}`}>
          <div className="h-5 w-5 rounded-full bg-white/20 flex items-center justify-center text-xs font-bold">{n}</div>
          <span className="hidden sm:inline text-xs font-semibold">{["Select PO", "Details", "Quantities"][n - 1]}</span>
        </div>
        {n < 3 && <div className={`flex-1 h-0.5 ${step > n ? "bg-[#0a2a5e]" : "bg-slate-200"}`} />}
      </React.Fragment>
    ))}
  </div>
);


const TotalRow = ({ label, value, bold = false, big = false, testid }) => (
  <div className={`flex items-center justify-between ${big ? "text-base" : "text-sm"} ${bold || big ? "font-bold text-[#0a2a5e]" : "text-slate-700"} py-0.5`} data-testid={testid}>
    <span>{label}</span>
    <span>₹ {Number(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
  </div>
);
